<template>
  <div class="profile-header">
    <!-- 頭像區塊 -->
    <div class="avatar-section">
      <div class="avatar" @click="triggerFilePicker">
        <img :src="userData.avatar" @error="avatarError" alt="User Avatar" />
      </div>
      <input ref="fileInput" type="file" accept=".png, .jpg, .jpeg" @change="handleFileChange" style="display: none" />
      <div class="avatar_limit">
        <p>
          {{ $t('member-information.key020') }}
          <br />
          {{ $t('member-information.key021') }}
        </p>
        <p class="remove" @click.stop="removeAvatar">
          {{ $t('member-information.key022') }}
        </p>
      </div>
    </div>

    <!-- 裁剪彈窗 -->
    <div v-if="showCropper" class="cropper-modal">
      <div class="mask"></div>
      <div class="cropper">
        <cropper :src="cropperImage" :stencil-props="{ aspectRatio: 1 / 1 }" @change="updateCroppedImage" :auto-zoom="true" />
        <div class="btn_box">
          <button type="button" class="button_outline" @click="closeCropper">
            {{ $t('member-information.key023') }}
          </button>
          <button type="button" class="button_style_linear" @click="confirmCrop">
            {{ $t('member-information.key024') }}
          </button>
        </div>
      </div>
    </div>

    <!-- 會員基本資訊(不可修改的部分) -->
    <div class="profile-info">
      <div class="profile-info-label">
        <label>會員 ID</label>
        <div class="copy-box">
          <p>{{ userData.uid }}</p>
          <div class="copy" @click="copyToClipboard(userData.uid)">
            <img src="@/assets/img/member/copy.svg" alt="" />
          </div>
        </div>
      </div>
      <div class="profile-info-label">
        <label>登入方式</label>
        <div class="loginMethod">
          <div
            v-for="method in loginMethods"
            :key="method.id"
            :class="[
              'icon',
              {
                active: method.id === userData.currentLoginMethod,
              },
            ]"
          >
            <img :src="method.icon" :alt="`Login Method ${method.id}`" />
          </div>
        </div>
      </div>
      <div class="profile-info-label">
        <label>註冊信箱</label>
        <div class="copy-box">
          <p>{{ userData.account }}</p>
          <div class="copy" @click="copyToClipboard(userData.account)">
            <img src="@/assets/img/member/copy.svg" alt="" />
          </div>
        </div>
      </div>
    </div>
    <div class="copySuccess">{{ $t('member-information.key054') }}</div>
  </div>
</template>

<script>
import { ref, inject, computed } from 'vue';
import { useStore } from 'vuex';
import { imageUploader } from '@/utils/imageUploader';
import { getPresignedUrl, removeAvatar as removeAvatarApi, removeAvatarCache } from '@/api/apiService';
import { Cropper } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';

export default {
  components: {
    Cropper,
  },
  setup() {
    const userData = inject('userData');
    const loading = inject('loading');
    const store = useStore();
    const jwt = computed(() => store.state.jwt);
    const loginMethods = [
      {
        id: 1,
        icon: require('@/assets/img/member/loginMethod_beatday.svg'),
      },
      {
        id: 2,
        icon: require('@/assets/img/member/loginMethod_google.svg'),
      },
      {
        id: 3,
        icon: require('@/assets/img/member/loginMethod_facebook.svg'),
      },
      {
        id: 4,
        icon: require('@/assets/img/member/loginMethod_apple.svg'),
      },
      {
        id: 5,
        icon: require('@/assets/img/member/loginMethod_discord.svg'),
      },
    ];

    const fileInput = ref(null);
    const previewAvatar = ref(userData.avatar);
    const tempFile = ref(null);
    const cropperImage = ref(null);
    const croppedImage = ref(null);
    const showCropper = ref(false);
    const tempAvatar = ref(null);
    const DEFAULT_AVATAR = 'https://d2uz35uzla1hiz.cloudfront.net/UserIcon/ch.png';

    const avatarError = (event) => {
      event.target.src = DEFAULT_AVATAR;
    };

    // **動態生成頭像 URL**
    const getImageUrl = (uid) => {
      const path = process.env.VUE_APP_USER_AVATAR_URL;
      return `${path}${uid}.png`;
    };

    // 點擊頭像觸發文件選擇器
    const triggerFilePicker = () => {
      fileInput.value?.click();
    };

    // 頭像上傳處理
    const handleFileChange = (event) => {
      const file = event.target.files[0];
      if (!file) return;

      // 檢查檔案類型 & 大小
      if (['image/png', 'image/jpeg'].includes(file.type) && file.size <= 5 * 1024 * 1024) {
        tempFile.value = file;
        cropperImage.value = URL.createObjectURL(file);
        showCropper.value = true;
      } else {
        alert(this.$t('member-information.key066'));
      }
    };

    // 更新裁剪後的圖片
    const updateCroppedImage = ({ canvas }) => {
      canvas.toBlob((blob) => {
        croppedImage.value = blob;
      });
    };

    // 確認裁剪後的圖片
    const confirmCrop = async () => {
      try {
        loading.value = true;

        if (croppedImage.value) {
          const compressedImage = await compressImage(croppedImage.value, 64, 64);
          previewAvatar.value = URL.createObjectURL(compressedImage);
          tempAvatar.value = compressedImage;
        }
        await uploadAvatar();

        closeCropper();
      } catch (error) {
        console.error(error);
      } finally {
        loading.value = false;
      }
    };

    // 關閉裁剪彈窗
    const closeCropper = () => {
      showCropper.value = false;
      cropperImage.value = null;
      croppedImage.value = null;
      tempFile.value = null;

      if (fileInput.value) fileInput.value.value = '';
    };

    // 壓縮圖片
    const compressImage = (image, width, height) => {
      return new Promise((resolve) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = width;
        canvas.height = height;

        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            resolve(blob);
          }, 'image/png');
        };
        img.src = URL.createObjectURL(image);
      });
    };

    // 上傳頭像
    const uploadAvatar = async () => {
      try {
        const fileType = 'png';
        const response = await getPresignedUrl(fileType, jwt.value);
        const { data: presignedUrl } = response.data;
        const file = tempAvatar.value;

        // 上傳到 S3
        await imageUploader.s3Uploader.uploadToS3(file, presignedUrl);

        // 手動更新頭貼
        userData.value.avatar = getImageUrl(userData.value.uid) + `?t=${Date.now()}`;

        // 清除頭像快取
        await removeAvatarCache(fileType, jwt.value);
      } catch (error) {
        console.error('頭像上傳失敗', error);
      }
    };

    // 移除頭像
    const removeAvatar = async () => {
      try {
        loading.value = true;
        const fileType = 'png';
        await removeAvatarApi(fileType, jwt.value);

        previewAvatar.value = DEFAULT_AVATAR;
        userData.value.avatar = DEFAULT_AVATAR;

        if (fileInput.value) fileInput.value.value = '';
      } catch (error) {
        alert(this.$t('member-information.key088'));
      } finally {
        loading.value = false;
      }
    };

    const copyToClipboard = (text) => {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          $('.copySuccess').css('animation-name', 'copySuccess');
          setTimeout(() => {
            $('.copySuccess').css('animation-name', '');
          }, 2000);
        })
        .catch((err) => {
          console.error(this.$t('my-orders.key023'), err);
        });
    };

    return {
      userData,
      loginMethods,
      copyToClipboard,
      previewAvatar,
      triggerFilePicker,
      handleFileChange,
      removeAvatar,
      updateCroppedImage,
      confirmCrop,
      closeCropper,
      compressImage,
      uploadAvatar,
      fileInput,
      cropperImage,
      showCropper,
      loading,
      avatarError,
    };
  },
};
</script>

<style lang="scss">
.profile-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  .avatar-section {
    width: 30%;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    gap: 10px;
    padding: 10px;
  }

  .avatar {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    overflow: hidden;
    position: relative;
    border: 1px solid #fff;
    &::after {
      content: '';
      width: 100px;
      height: 100px;
      border-radius: 50%;
      position: absolute;
      left: 0;
      top: 0;
      background-color: rgb(44, 44, 44, 0.8);
      background-image: url('/src/assets/img/member/camera.svg');
      background-repeat: no-repeat;
      background-position: center center;
      background-size: 40%;
      opacity: 0;
      transition: 0.4s;
      cursor: pointer;
    }

    &:hover::after {
      opacity: 0.8;
    }
  }

  .avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .avatar_limit {
    font-size: 12px;
    color: #666;
    margin-top: 5px;
  }

  .remove {
    color: #919191;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  .profile-info-label {
    margin-bottom: 10px;

    .copy-box {
      display: flex;

      .copy {
        margin-left: 5px;
      }
    }
  }

  .profile-info label {
    font-weight: bold;
    font-size: 14px;
  }

  .profile-info .loginMethod {
    margin-top: 5px;
    display: flex;

    .icon {
      margin-right: 10px;

      img {
        opacity: 0.2;
        width: 2rem;
      }
    }

    .icon.active img {
      opacity: 1;
      filter: none;
    }
  }

  .profile-info p {
    margin: 2px 0 10px;
    font-size: 14px;
  }

  .copySuccess {
    pointer-events: none;
    @include position-fixed-center;
    position: fixed;
    background-color: rgba(40, 40, 40, 0.8);
    backdrop-filter: blur(3px);
    padding: 20px 80px;
    opacity: 0;
    animation-duration: 2s;
  }

  @keyframes copySuccess {
    0% {
      opacity: 0;
    }

    10% {
      opacity: 1;
    }

    50% {
      opacity: 1;
    }

    100% {
      opacity: 0;
    }
  }

  .cropper-modal {
    position: fixed;
    height: 100vh;
    width: 100vw;
    z-index: 1000;

    .btn_box {
      display: flex;
      justify-content: space-between;
      z-index: $z-index-modal;

      button {
        width: 45%;
      }

      .button_outline {
        border: 1px solid #fff;
        border-radius: 5px;
        padding: 10px;
        margin-top: 20px;
      }
    }

    .mask {
      backdrop-filter: blur(3px);
    }

    .cropper {
      @include position-fixed-center;
      position: fixed;
      z-index: $z-index-toast;

      @media (max-width: 576px) {
        width: 90%;

        .vue-advanced-cropper__stretcher {
          max-height: 80vh;
        }
      }
    }
  }
  @media (max-width: 441px) {
    flex-direction: column;
    .avatar-section {
      width: 100%;
      flex-direction: row;
    }
  }
}
</style>
